import { computed } from 'vue'

import { useCookie, useRoute } from 'nuxt/app'

import { useI18n } from 'vue-i18n'

import { useAppAccess } from '@common/app-access-layer/composables/useAppAccess'
import { useVibeHost } from '@common/location-layer/composables/useVibeHost'

import CPPortalSidebarIcon from '../components/CPPortalSidebarIcon.vue'
import { NAVIGATION_ITEMS, NAV_GROUP_LABEL_KEYS, NAV_GROUP_ORDER, type PortalNavGroupId } from '../constants/navigation'
import type { PortalNavigationGroup, PortalNavigationItem } from '../types/portal-sidebar'

const GROUP_RANK: Record<PortalNavGroupId, number> = {
  dashboard: 0,
  learningCommunity: 1,
  financeBilling: 2,
  businessOperations: 3,
}

const NAV_ITEM_IDS_HIDDEN_ON_MOBILE = new Set(NAVIGATION_ITEMS.filter(item => item.hideOnMobile).map(item => item.id))

/**
 * Partition already-sorted accessible items into sidebar bands (skips empty groups).
 * @internal exported for unit tests
 */
export function partitionNavigationGroups(sortedItems: PortalNavigationItem[]): PortalNavigationGroup[] {
  const groups: PortalNavigationGroup[] = []

  const dashboardItems = sortedItems.filter(i => i.navGroup === 'dashboard')
  if (dashboardItems.length > 0) {
    groups.push({ groupId: 'dashboard', items: dashboardItems })
  }

  for (const groupId of NAV_GROUP_ORDER) {
    const groupItems = sortedItems.filter(i => i.navGroup === groupId)
    if (groupItems.length > 0) {
      groups.push({
        groupId,
        labelKey: NAV_GROUP_LABEL_KEYS[groupId],
        items: groupItems,
      })
    }
  }

  return groups
}

/**
 * Portal sidebar (AppSidebar) state and nav for CPRootContainer.
 * Branding, navigation items, selection, and collapse state.
 */
export function usePortalSidebar() {
  const route = useRoute()
  const { t } = useI18n()
  const { getAccessibleNavigationItems } = useAppAccess()
  // On a vibe host ClientPortal serves only Communities; the vibe app owns every other page.
  const isVibeHost = useVibeHost()

  const isCollapsed = useCookie<boolean>('cp:sidebar-collapsed', {
    // SSR can read cookies on the request, so first render matches persisted state.
    default: () => false,
  })
  const toggleSidebar = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const configOrderIndex = computed(() => new Map(NAVIGATION_ITEMS.map((c, idx) => [c.id, idx])))

  const allNavigationItems = computed<PortalNavigationItem[]>(() =>
    NAVIGATION_ITEMS.filter(item => !item.hidden && (!isVibeHost || item.id === 'communities')).map(item => ({
      id: item.id,
      name: t(item.translationKey),
      icon: CPPortalSidebarIcon,
      iconName: item.iconName,
      fallbackIcon: item.fallbackIcon,
      routeName: item.routeName,
      routePath: item.routePath,
      navGroup: item.navGroup,
    }))
  )

  const navigationItems = computed<PortalNavigationItem[]>(() => {
    const accessible = getAccessibleNavigationItems(allNavigationItems.value)
    const orderMap = configOrderIndex.value
    return [...accessible].sort((a, b) => {
      const rank = GROUP_RANK[a.navGroup] - GROUP_RANK[b.navGroup]
      if (rank !== 0) {
        return rank
      }
      return (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
    })
  })

  const navigationGroups = computed<PortalNavigationGroup[]>(() => partitionNavigationGroups(navigationItems.value))

  const navigationItemsMobile = computed(() => navigationItems.value.filter(item => !NAV_ITEM_IDS_HIDDEN_ON_MOBILE.has(item.id)))

  const navigationGroupsMobile = computed<PortalNavigationGroup[]>(() => partitionNavigationGroups(navigationItemsMobile.value))

  const selectedItem = computed<PortalNavigationItem | undefined>(() =>
    navigationItems.value.find(item => route.path === item.routePath || route.path.startsWith(item.routePath + '/'))
  )

  const currentPageTitle = computed(() => selectedItem.value?.name || t('clientportal.navigation.dashboard'))

  return {
    isCollapsed,
    navigationItems,
    navigationGroups,
    navigationGroupsMobile,
    selectedItem,
    currentPageTitle,
    toggleSidebar,
  }
}
